import React, { useEffect } from 'react';
import { submitFormData } from '../../../../pages/api';

const AutoBussing = ({ formData, onFormDataChange }) => {
    const { solderingData = [], bussingData = [], solderingObservations = [], bussingObservations = [], nonConformance = '' } = formData;

    useEffect(() => {
        // Destructure formData to get initial values
        const { solderingData = [], bussingData = [], solderingObservations = [], bussingObservations = [], nonConformance = '' } = formData;
    
        // Check if formData already has prefilled data
        const hasFormData =
            solderingData.length > 0 ||
            bussingData.length > 0 ||
            solderingObservations.length > 0 ||
            bussingObservations.length > 0 ||
            nonConformance.trim() !== '';
    
        if (!hasFormData) {
            // If no formData, check metadata and inspectionData
            const metadata = JSON.parse(sessionStorage.getItem('metadata'));
            const inspectionData = JSON.parse(sessionStorage.getItem('inspectionData'));
    
            let prefilledSolderingData = [];
            let prefilledBussingData = [];
            let prefilledSolderingObservations = [];
            let prefilledBussingObservations = [];
            let prefilledNonConformance = '';
    
            // Check metadata for prefilled data
            if (metadata && metadata.pre && metadata.pre.autoBussing_pre) {
                prefilledSolderingData = metadata.pre.autoBussing_pre.solderingCurrent || [];
                prefilledBussingData = metadata.pre.autoBussing_pre.bussing || [];
                prefilledSolderingObservations = prefilledSolderingData.map(() => ({ observation1: '', observation2: '' }));
                prefilledBussingObservations = prefilledBussingData.map(() => ({ observation1: '', observation2: '' }));
            }
    
            // Check inspectionData for prefilled data (overrides metadata if present)
            if (
                inspectionData &&
                inspectionData.PreLamination &&
                inspectionData.PreLamination.autoBussing_pre
            ) {
                const { solderingObservations: inspectionSolderingObs, bussingObservations: inspectionBussingObs, nonConformance: inspectionNonConformance } =
                    inspectionData.PreLamination.autoBussing_pre;
    
                prefilledSolderingData = inspectionSolderingObs || [];
                prefilledBussingData = inspectionBussingObs || [];
                prefilledSolderingObservations = inspectionSolderingObs.map(item => ({
                    observation1: item.observation1 || '',
                    observation2: item.observation2 || '',
                }));
                prefilledBussingObservations = inspectionBussingObs.map(item => ({
                    observation1: item.observation1 || '',
                    observation2: item.observation2 || '',
                }));
                prefilledNonConformance = inspectionNonConformance || '';
            }
    
            // Update formData only if prefilled data exists
            if (
                prefilledSolderingData.length > 0 ||
                prefilledBussingData.length > 0 ||
                prefilledSolderingObservations.length > 0 ||
                prefilledBussingObservations.length > 0 ||
                prefilledNonConformance.trim() !== ''
            ) {
                onFormDataChange({
                    ...formData,
                    solderingData: prefilledSolderingData,
                    bussingData: prefilledBussingData,
                    solderingObservations: prefilledSolderingObservations,
                    bussingObservations: prefilledBussingObservations,
                    nonConformance: prefilledNonConformance,
                });
            }
        }
    }, [formData, onFormDataChange]);
    
    const handleObservationChange = (index, field, value, isSoldering) => {
        const updatedObservations = isSoldering ? [...solderingObservations] : [...bussingObservations];
        updatedObservations[index][field] = value;
        onFormDataChange({
            ...formData,
            [isSoldering ? 'solderingObservations' : 'bussingObservations']: updatedObservations
        });
    };

    const submitObservations = async () => {
        const solderingObs1Missing = solderingObservations.some(obs => !obs.observation1 || obs.observation1.trim() === '');
        const bussingObs1Missing = bussingObservations.some(obs => !obs.observation1 || obs.observation1.trim() === '');
    
        if (solderingObs1Missing || bussingObs1Missing) {
            alert('Please fill in all Observation 1 fields before submitting.');
            return;
        }
    
        const projectId = localStorage.getItem('selectedProject');
        const inspectionDate = sessionStorage.getItem('inspectionDate');
        const inspectionShift = sessionStorage.getItem('inspectionShift');
        const inspectorName = sessionStorage.getItem('username');       
        const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');
        
        const formattedSolderingObservations = solderingData.map((item, index) => ({
            product: item.product,
            specifications: item.specifications,
            frequency: item.frequency,
            observation1: solderingObservations[index]?.observation1 || '',
            observation2: solderingObservations[index]?.observation2 || '',
        }));
        
        const formattedBussingObservations = bussingData.map((item, index) => ({
            product: item.product,
            specifications: item.specifications,
            frequency: item.frequency,
            observation1: bussingObservations[index]?.observation1 || '',
            observation2: bussingObservations[index]?.observation2 || '',
        }));
        
        const payload = {
            projectId,
            inspectionSupplier,
            inspectionDate,
            inspectionShift,
            inspectorName,
            status: 'completed',
            PreLamination: {
                autoBussing_pre: {
                    solderingObservations: formattedSolderingObservations,
                    bussingObservations: formattedBussingObservations,
                    nonConformance
                },
            }
        };
        
        try {
            await submitFormData(payload);
            alert('Observations submitted successfully');
        } catch (error) {
            console.error('Error submitting observations:', error);
            alert('Failed to submit observations');
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Auto Bussing Inspection</h3>
            
            {/* First Table: Soldering Current */}
            <h4 className="text-lg font-semibold mt-6 mb-4 text-gray-700">Soldering Current</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg border-collapse table-auto">
                    <thead>
                        <tr className="bg-[#424752] text-white text-center">
                            <th className="py-3 px-4 min-w-[150px] rounded-tl-lg">Product</th>
                            <th className="py-3 px-4 min-w-[200px]">Specifications</th>
                            <th className="py-3 px-4 min-w-[150px]">Frequency</th>
                            <th className="py-3 px-4 min-w-[200px]">Observation 1</th>
                            <th className="py-3 px-4 min-w-[200px] rounded-tr-lg">Observation 2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {solderingData.map((item, index) => (
                            <tr
                                key={index}
                                className={`border-b last:border-none ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-100 transition-colors duration-200`}
                            >
                                <td className="py-3 px-4">{item.product}</td>
                                <td className="py-3 px-4">{item.specifications}</td>
                                <td className="py-3 px-4">{item.frequency}</td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={solderingObservations[index]?.observation1 || ''}
                                        onChange={(e) => handleObservationChange(index, 'observation1', e.target.value, true)}
                                        placeholder="Enter observation 1"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                        required
                                    />
                                </td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={solderingObservations[index]?.observation2 || ''}
                                        onChange={(e) => handleObservationChange(index, 'observation2', e.target.value, true)}
                                        placeholder="Enter observation 2"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Second Table: Bussing */}
            <h4 className="text-lg font-semibold mt-6 mb-4 text-gray-700">Bussing</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg border-collapse table-auto">
                    <thead>
                        <tr className="bg-[#424752] text-white text-center">
                            <th className="py-3 px-4 min-w-[150px] rounded-tl-lg">Product</th>
                            <th className="py-3 px-4 min-w-[200px]">Specifications</th>
                            <th className="py-3 px-4 min-w-[150px]">Frequency</th>
                            <th className="py-3 px-4 min-w-[200px]">Observation 1</th>
                            <th className="py-3 px-4 min-w-[200px] rounded-tr-lg">Observation 2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {bussingData.map((item, index) => (
                            <tr
                                key={index}
                                className={`border-b last:border-none ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-100 transition-colors duration-200`}
                            >
                                <td className="py-3 px-4">{item.product}</td>
                                <td className="py-3 px-4">{item.specifications}</td>
                                <td className="py-3 px-4">{item.frequency}</td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={bussingObservations[index]?.observation1 || ''}
                                        onChange={(e) => handleObservationChange(index, 'observation1', e.target.value, false)}
                                        placeholder="Enter observation 1"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                        required
                                    />
                                </td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={bussingObservations[index]?.observation2 || ''}
                                        onChange={(e) => handleObservationChange(index, 'observation2', e.target.value, false)}
                                        placeholder="Enter observation 2"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Non Conformance Input */}
            <div className="mt-6">
                <h4 className="text-lg font-semibold mb-2 text-gray-700">Non Conformance</h4>
                <textarea
                    value={nonConformance}
                    onChange={(e) => onFormDataChange({ ...formData, nonConformance: e.target.value })}
                    placeholder="Enter details about non conformance..."
                    className="w-full border border-gray-300 p-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                    rows={4}
                />
            </div>

            {/* Submit Observations Button */}
            <button
                onClick={submitObservations}
                className="mt-4 px-6 py-2 bg-[#424752] text-white rounded-lg hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
            >
                Submit
            </button>
        </div>
    );
};

export default AutoBussing;